import type { ReactNode } from "react";

import { Map as MapView } from "../../../map/Map";
import { UseMapCenterButton } from "../../../views/map-view/map-pane/use-map-center-button";

type MapPlacementSectionProps = {
	center?: [number, number];
	fitBoundsKey?: string;
	hasPartialCoordinates: boolean;
	/** Optional leading toolbar content (e.g. edit-mode zone hint). */
	leadingToolbar?: ReactNode;
	markers?: GeoJSON.Feature[];
	markersInteractive?: boolean;
	onMapClick?: (coordinates: [number, number]) => void;
	onMarkerClick?: (feature: GeoJSON.Feature) => void;
	onMarkerDragEnd?: (
		feature: GeoJSON.Feature,
		coordinates: [number, number],
	) => void;
	onMoveEnd?: (center: [number, number]) => void;
	onUseMapCenter?: () => void;
	overlays?: GeoJSON.Feature[] | GeoJSON.FeatureCollection;
	/** Shown when only one of lat/lng is filled. */
	partialCoordinatesMessage?: string;
	showUseMapCenter?: boolean;
	/**
	 * When true (default), pin popups are suppressed. Edit-sensor flows that
	 * switch target on marker click keep this true and use `onMarkerClick`.
	 */
	suppressPopup?: boolean;
};

/**
 * Shared map preview + Use map center + partial-coordinate validation chrome
 * for location and sensor create/edit dialogs.
 */
export const MapPlacementSection = ({
	center,
	fitBoundsKey,
	hasPartialCoordinates,
	leadingToolbar,
	markers,
	markersInteractive = false,
	onMapClick,
	onMarkerClick,
	onMarkerDragEnd,
	onMoveEnd,
	onUseMapCenter,
	overlays,
	partialCoordinatesMessage = "Enter both latitude and longitude to map this location.",
	showUseMapCenter = false,
	suppressPopup = true,
}: MapPlacementSectionProps) => (
	<>
		{(leadingToolbar || showUseMapCenter) && (
			<div
				className={
					leadingToolbar
						? "flex items-center justify-between gap-2"
						: "flex items-center justify-end"
				}
			>
				{leadingToolbar}
				{onUseMapCenter ? (
					<UseMapCenterButton
						onClick={onUseMapCenter}
						visible={showUseMapCenter}
					/>
				) : null}
			</div>
		)}
		{hasPartialCoordinates ? (
			<p className="text-sm text-error-primary">{partialCoordinatesMessage}</p>
		) : null}
		<div className="h-80 w-full shrink-0 overflow-hidden rounded-lg">
			<MapView
				center={center}
				compact
				fitBoundsKey={fitBoundsKey}
				markers={markers}
				markersInteractive={markersInteractive}
				onMapClick={onMapClick}
				onMarkerClick={onMarkerClick}
				onMarkerDragEnd={onMarkerDragEnd}
				onMoveEnd={onMoveEnd}
				overlays={overlays}
				refitOnMarkerChange={false}
				scrollZoom={false}
				suppressPopup={suppressPopup}
			/>
		</div>
	</>
);
